import { useQuery } from '@tanstack/react-query';
import { adminApi, type AgentRole, type AgentType } from '../api/admin';
import { roleLabel as staticRoleLabel } from './agent-roles';

export interface RoleOption {
  value: AgentRole;
  label: string;
}
export interface TypeOption {
  value: AgentType;
  label: string;
}

// 区分(type)・役割(role) の選択肢とラベルを「マスタ管理(#176 agent_types / agent_roles_master)」から供給する共通フック。
// チーム・テンプレ・エージェントはこの区分/役割コードを使う（DBも String 化済み）。
// 静的な ROLE_OPTIONS（agent-roles.ts）はマスタ未取得時のラベルフォールバックとしてのみ使う。
export function useAgentRoles() {
  const mastersQuery = useQuery({ queryKey: ['agent-masters'], queryFn: adminApi.listAgentMasters });
  const masterTypes = mastersQuery.data?.types ?? [];

  // 有効な区分の選択肢。
  const typeOptions: TypeOption[] = masterTypes
    .filter((t) => t.enabled)
    .map((t) => ({ value: t.code, label: t.label }));

  // 区分ラベル（無効含む全区分から引く）。
  const typeLabelOf = (code: AgentType): string =>
    masterTypes.find((t) => t.code === code)?.label ?? code;

  // 指定区分に属する有効な役割の選択肢（カスケード）。
  const roleOptionsByType = (typeCode: AgentType): RoleOption[] => {
    const type = masterTypes.find((t) => t.code === typeCode);
    if (!type) return [];
    return type.roles
      .filter((r) => r.enabled)
      .map((r) => ({ value: r.code, label: r.label }));
  };

  // 区分横断の有効役割（重複コードは除く）。区分を問わない場面用。
  const roleOptions: RoleOption[] = [];
  const seen = new Set<string>();
  for (const t of masterTypes) {
    for (const r of t.roles) {
      if (!r.enabled || seen.has(r.code)) continue;
      seen.add(r.code);
      roleOptions.push({ value: r.code, label: r.label });
    }
  }

  // ラベルは無効も含む全役割から引く → 既存スロットが無効化済みの役割でも表示は崩れない。
  const roleLabelOf = (code: AgentRole): string => {
    if (code === 'unset') return '未設定';
    for (const t of masterTypes) {
      const hit = t.roles.find((r) => r.code === code);
      if (hit) return hit.label;
    }
    return staticRoleLabel(code);
  };

  return {
    typeOptions,
    typeLabelOf,
    roleOptions,
    roleOptionsByType,
    roleLabelOf,
    isLoading: mastersQuery.isLoading,
  };
}
